<template>
  <Teleport to="body" :disabled="inline">
    <Transition name="sheet" appear @after-leave="onAfterLeave">
      <dialog
        v-if="isOpen"
        v-bind="$attrs"
        :ref="bindDialog"
        class="sheet-dialog"
        :class="{ 'sheet-dialog--inline': inline }"
        :aria-labelledby="title ? titleId : undefined"
        @cancel.prevent="close"
        @click="close"
      >
        <div
          ref="sheetEl"
          autofocus
          tabindex="-1"
          class="sheet"
          :class="{
            'sheet--fullscreen': fullscreen,
            'sheet--dragging': dragging,
          }"
          :style="sheetStyle"
          @click.stop
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="endDrag"
          @pointercancel="endDrag"
        >
          <div v-if="!fullscreen" class="sheet__grabber" aria-hidden="true" />

          <header v-if="fullscreen || title || $slots.header" class="sheet__head">
            <slot name="header" :close="close">
              <h2 v-if="title" :id="titleId" class="sheet__title">{{ title }}</h2>
            </slot>
            <button
              v-if="fullscreen"
              type="button"
              class="sheet__close"
              :aria-label="closeLabel"
              @click="close"
            >
              <i class="pi pi-times" />
            </button>
          </header>

          <section class="sheet__body">
            <slot :close="close" />
          </section>
        </div>
      </dialog>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, useId } from 'vue';

import { dismissesOnRelease, gestureVerdict } from '../../utils/sheetGesture';
import { NAMED_HEIGHTS, isSheetHeight } from '../../utils/sheetShapes';

defineOptions({
  name: 'Sheet',
  // The ARIA name of a sheet with no `title` is the consumer's to supply, and
  // an `aria-label` written at the call site has to land on the <dialog> rather
  // than on the Teleport that is this component's root. Bound explicitly below
  // for that reason; see `aria-labelledby` and the description in the stories.
  inheritAttrs: false,
});

/**
 * Open. Named rather than anonymous — `modelValue` reads acceptably as
 * `v-model="isOpen"` and says nothing at all at the two call sites that bind
 * the long form, where `:model-value="!!removeTarget"` gives a reader nothing
 * to go on. See rule 5 of vue-coding-standards.md.
 *
 * Two-way in form and one-and-a-half-way in fact: a sheet cannot open itself,
 * so `update:open` only ever carries `false`. Openness flows down; only
 * closure flows back.
 */
const isOpen = defineModel('open', { type: Boolean, default: false });

const props = defineProps({
  /**
   * The sheet's heading, and what names the dialog — `aria-labelledby` points
   * at it. A sheet with no title is unnamed unless the call site passes an
   * `aria-label`, which falls through to the <dialog>.
   */
  title: { type: String, default: '' },
  /**
   * The resting height: `auto` fits the content, `medium` and `large` are the
   * two named stops, and any CSS length is accepted for a sheet that needs one
   * of its own. A change animates, except to or from `auto` — CSS cannot
   * interpolate a keyword, and the snap is accepted rather than measured
   * around.
   */
  height: {
    type: String,
    default: 'auto',
    validator: isSheetHeight,
  },
  /**
   * Fill the viewport: edge to edge, no rounded top, no grabber, no drag — and
   * a close button, because there is no backdrop left to tap. The whole
   * presentation, from one flag; see the description in the stories for why
   * none of it is separately settable.
   */
  fullscreen: { type: Boolean, default: false },
  /** The close button's accessible name. Read only under `fullscreen`. */
  closeLabel: { type: String, default: 'Close' },
  /**
   * Render the panel in place instead of as a modal over the page: no
   * teleport, no top layer, no backdrop, no focus trap, no scroll lock, no
   * drag-to-dismiss. An inline sheet is an exhibit of the panel, not a modal —
   * it is for surfaces that document the sheet (a catalog wall, a docs page),
   * never for the app, where a sheet that does not own the page is not a
   * sheet.
   */
  inline: { type: Boolean, default: false },
});

const emit = defineEmits(['after-close']);

const titleId = useId();

const close = () => {
  isOpen.value = false;
};

// ---- the dialog --------------------------------------------------------
// Escape, focus entry, the focus trap and an inert background behind the top
// layer all come from <dialog> opened with showModal().
//
// `autofocus` + `tabindex="-1"` on the panel, because <dialog>'s own focusing
// steps otherwise land on the first focusable descendant — which on a
// confirmation is the destructive row, one Enter away from a keyboard user who
// has only just opened the sheet. Focus goes to the panel instead: the dialog
// announces its own name, and Tab is what reaches the content. Three things do not and
// are written here: the dialog's name (aria-labelledby, above), the scroll
// lock, and the restore of focus to whatever opened the sheet.
//
// That last one is the element's job in principle — a <dialog> restores focus
// when close() runs. It cannot be taken here, because close() must not run
// until the leave transition has finished, and by then Vue has detached the
// node and focus has already fallen to <body>. So the opener is remembered on
// the way in and put back on the way out.
//
// Driven off a function ref rather than the Transition's enter hook. The hook
// would read better and is the wrong place: a stubbed <Transition> — which is
// Vue Test Utils' default, and any consumer's `<Transition>`-free render — then
// mounts a sheet that is a <dialog> in markup and modal in nothing else, with
// no Escape, no trap, and a page still scrolling behind it. A function ref is
// called with the element the moment it is in the document and with null the
// moment it is out, which is exactly the two edges this has to sit on.
let dialogElement = null;
let opener = null;
let overflowBeforeOpen = '';

const releasePage = () => {
  document.body.style.overflow = overflowBeforeOpen;
};

/**
 * showModal() throws on an element that is not in the document, and a stubbed
 * <Transition> — Vue Test Utils' default — holds the dialog inside a
 * `<transition-stub>` that is not attached yet when the ref fires. One tick
 * later it is. Worth carrying rather than declaring unsupported: a consumer
 * whose own tests stub transitions would otherwise get an uncaught
 * InvalidStateError out of a component they only wanted to render.
 */
const openModally = (element) => {
  if (element.open) return;
  if (element.isConnected) element.showModal();
  else nextTick(() => { if (element.isConnected && !element.open) element.showModal(); });
};

const bindDialog = (element) => {
  // An inline sheet takes none of the modal machinery: the element is already
  // visible through `.sheet-dialog`'s own `display` (author styles beat the
  // user agent's `dialog:not([open])`), and there is no page to lock, no
  // opener to remember and no top layer to enter.
  if (props.inline) return;
  // Vue re-invokes a function ref on EVERY patch of its element, not only when
  // the element arrives — so without this the first drag frame re-runs the open
  // branch, records the sheet's own dialog as the opener and the already-locked
  // `hidden` as the value to restore. Both failures are silent, and the second
  // one leaves the page unscrollable for the rest of the session.
  if (element === dialogElement) return;
  dialogElement = element;

  if (element) {
    opener = document.activeElement;
    overflowBeforeOpen = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    openModally(element);
    return;
  }

  // The page comes back now; the opener does not, and cannot. Vue clears an
  // element's ref BEFORE it detaches the node, so at this instant the dialog is
  // still in the top layer and everything outside it — the opener included — is
  // still inert. focus() called here silently does nothing, and a tick later it
  // still does: the leave transition runs for another 320ms. `after-leave` is
  // the first moment the node is genuinely gone.
  releasePage();
};

const onAfterLeave = () => {
  if (opener?.isConnected) opener.focus();
  opener = null;
  dragY.value = 0;
  dragging.value = false;
  emit('after-close');
};

// A sheet torn down while open never nulls its ref through the watcher, and the
// page it locked would stay locked.
onUnmounted(releasePage);

// ---- swipe-down to dismiss --------------------------------------------
// The offset is applied through the `--sheet-drag` custom property on the base
// `.sheet` transform (NOT an inline transform) so the leave transition's
// `translateY(100%)` rule still wins on dismiss — the slide then continues
// smoothly from wherever the finger let go. Past the threshold we close; short
// of it we animate back to rest.
//
// The two decisions inside it — is this a drag at all, and does this release
// dismiss — are in utils/sheetGesture.js, where a test can ask them directly.
// What is left here is plumbing.
//
// `height` rides in the same transition string as `transform` so that
// `transition: 'none'` while a finger is down suppresses both and the panel
// keeps tracking 1:1.
const SLIDE = 'transform 320ms cubic-bezier(0.32, 0.72, 0, 1), height 320ms cubic-bezier(0.32, 0.72, 0, 1)';

const sheetEl = ref(null);
const dragY = ref(0);
const dragging = ref(false);
let startY = 0;
let startX = 0;
let tracking = false;
let scrollEl = null;

const sheetStyle = computed(() => {
  const style = {
    // No transition while the finger is down, so the sheet tracks 1:1 — and
    // `height` rides in the same string so a height arriving mid-drag is
    // suppressed with it.
    transition: dragging.value ? 'none' : SLIDE,
  };
  // Published as a custom property rather than set on `height` directly: an
  // inline `height` would beat the stylesheet's own cap. A named height
  // resolves through the map; any other accepted value is a CSS length and is
  // used as written.
  if (props.height !== 'auto') {
    style['--sheet-height'] = NAMED_HEIGHTS[props.height] ?? props.height;
  }
  if (dragY.value) style['--sheet-drag'] = `${dragY.value}px`;
  return style;
});

const onPointerDown = (event) => {
  // A fullscreen sheet is not draggable: it has an X instead, because there is
  // no backdrop behind it to tap. An inline one is not either: it is an
  // exhibit, and a drag that dismissed it would tear it out of the page that
  // is documenting it.
  if (props.fullscreen || props.inline) return;
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  startY = event.clientY;
  startX = event.clientX;
  tracking = true;
  dragging.value = false;
  // Only the scrollable body competes with the gesture; the panel's own parts
  // (grabber / header / padding) always drag.
  //
  // Which makes `.sheet__body` THE scroller, and a constraint on what a
  // consumer may put in the slot: a scroller of its own leaves this reading
  // permanently 0, and gestureVerdict — whose whole job is telling a dismissal
  // from a scroll — then calls every downward swipe a dismissal, including one
  // begun halfway down a list. Content that needs to pin something above the
  // scroll sticks it to this body rather than nesting a second one inside it;
  // SaveSheet's create-category bar is the worked example.
  scrollEl = event.target?.closest?.('.sheet__body');
};

const onPointerMove = (event) => {
  if (!tracking) return;
  const deltaY = event.clientY - startY;

  if (!dragging.value) {
    const verdict = gestureVerdict({
      deltaX: event.clientX - startX,
      deltaY,
      scrollTop: scrollEl?.scrollTop ?? 0,
    });
    if (verdict === 'wait') return;
    if (verdict === 'ignore') {
      tracking = false;
      return;
    }
    dragging.value = true;
    sheetEl.value?.setPointerCapture?.(event.pointerId);
  }

  dragY.value = Math.max(0, deltaY);
  event.preventDefault();
};

const endDrag = () => {
  if (!tracking) return;
  tracking = false;
  if (!dragging.value) return;
  const dismissing = dismissesOnRelease(dragY.value);
  dragging.value = false;
  if (dismissing) {
    // Keep the drag offset in place; the leave transition slides it the rest
    // of the way. `after-leave` clears it for the next open.
    close();
  } else {
    dragY.value = 0;
  }
};
</script>

<style scoped>
/* The sheet lives in the browser's top layer, which is what makes everything
   behind it inert and un-tabbable without a line of JavaScript. The element
   itself is the full-viewport click target — the backdrop tap — and the panel
   inside it stops its own clicks. `z-index` is deliberately absent: the top
   layer ignores it. */
.sheet-dialog {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
  outline: none;
  /* Keyboard is configured `resize: "none"`, so it overlays the page rather
     than shrinking it — and sheets anchor to the screen bottom (flex-end), so
     an open keyboard would sit on top of the sheet's lower half (footer
     inputs, autofocused fields). Reserve the published keyboard height here so
     the sheet floats onto the keyboard's top edge. This element (unlike
     `.sheet`, which carries an inline transform transition) can animate it
     freely, so we match iOS's keyboard show/hide timing. See
     services/keyboardLayout.js for where --keyboard-height is set, and the
     matching treatment on .conv in ConversationPage.vue. */
  padding-bottom: var(--keyboard-height, 0px);
  transition: padding-bottom 0.25s cubic-bezier(0.17, 0.59, 0.4, 0.77);
}

/* Inline: the panel in the flow of whatever mounted it. The element stops
   covering the viewport — which also empties the backdrop-tap close target —
   and the keyboard reserve goes with it, because an inline sheet floats over
   nothing. `::backdrop` needs no counterpart here: it only paints in the top
   layer, and an inline dialog never enters it. */
.sheet-dialog--inline {
  position: static;
  width: auto;
  height: auto;
  padding-bottom: 0;
  display: block;
}

/* The tint and blur, which used to be a fixed div of our own. Pure black at an
   alpha is the absence of a theme color rather than one of them, which is why
   it is a literal here and has no token. */
.sheet-dialog::backdrop {
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.sheet {
  position: relative;
  width: 100%;
  max-width: 640px;
  /* Cap to the space left above the keyboard so a tall sheet (e.g. one at the
     `large` height), once lifted by the dialog's padding, doesn't overflow off
     the top of the screen and clip its header/grabber. Collapses to 88vh when
     no keyboard is open — rounded to a whole pixel like the named heights in
     sheetShapes.js, and for the reason given there, so the cap and the `large`
     stop keep resolving to the same length. */
  max-height: min(round(88vh, 1px), calc(100vh - var(--keyboard-height, 0px)));
  /* Set from `sheetStyle`; absent at the `auto` height. */
  height: var(--sheet-height, auto);
  /* Lets the browsers that support it animate the `auto` legs too. Where it is
     unsupported, `auto` → a named height snaps and the rest animate. */
  interpolate-size: allow-keywords;
  background: var(--p-sheet-background);
  color: var(--p-sheet-color);
  font-family: var(--p-sheet-font-family);
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-top: var(--p-sheet-border-top-width) solid var(--p-sheet-border-top-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--p-sheet-box-shadow);
  padding-bottom: max(20px, env(safe-area-inset-bottom));
  /* Live swipe-down offset; the leave transition overrides this. */
  transform: translateY(var(--sheet-drag, 0px));
  will-change: transform;
  /* The panel's own parts (grabber / header / padding) are ours to drag — the
     browser must not steal the vertical pan, or it cancels the gesture before
     the sheet moves. Only the scrollable body opts back into pan-y below.
     touch-action is not inherited, so this doesn't affect the body. */
  touch-action: none;
  /* The panel holds focus on arrival but is not tabbable, so it has no keyboard
     affordance to lose by suppressing the ring. */
  outline: none;
}

/* While the finger is actively dragging, suppress the body's own panning so
   the sheet — not the content — follows the gesture. */
.sheet--dragging .sheet__body {
  touch-action: none;
}

/* Edge-to-edge slide-up modal. No cap, no rounded top — the consumer owns all
   content (incl. its own header) via the slots; the body is a positioned,
   padding-free container so a consumer's absolutely positioned overlay (e.g. a
   pushed sub-page) can fill it. */
.sheet--fullscreen {
  max-width: none;
  max-height: none;
  height: 100dvh;
  border-radius: 0;
  border-top: none;
  box-shadow: none;
  padding-bottom: 0;
}

/* A fullscreen sheet's header is at the very top of the screen and has to clear
   the status bar itself. A default sheet sits below one and does not. */
.sheet--fullscreen .sheet__head {
  padding-top: max(20px, env(safe-area-inset-top));
}

.sheet--fullscreen .sheet__body {
  padding: 0;
  position: relative;
}

.sheet__grabber {
  width: 40px;
  height: 5px;
  border-radius: var(--p-sheet-grabber-border-radius);
  background: var(--p-sheet-grabber-background);
  margin: 10px auto 0;
  flex-shrink: 0;
}

.sheet__head {
  position: relative;
  padding: var(--p-sheet-head-padding);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sheet__title {
  margin: 0;
  font-family: var(--p-sheet-title-font-family);
  font-size: var(--p-sheet-title-font-size);
  font-weight: var(--p-sheet-title-font-weight);
  letter-spacing: var(--p-sheet-title-letter-spacing);
  color: var(--p-sheet-title-color);
}

.sheet__close {
  position: absolute;
  right: 14px;
  top: 10px;
  width: 44px;
  height: 44px;
  border-radius: var(--p-sheet-close-border-radius);
  background: var(--p-sheet-close-background);
  border: none;
  color: var(--p-sheet-close-color);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.sheet__close i {
  font-size: var(--p-sheet-close-icon-font-size);
}

.sheet__body {
  flex: 1;
  overflow-y: auto;
  padding: var(--p-sheet-body-padding);
  /* What makes a flex child scroll rather than grow. */
  min-height: 0;
  /* Body scrolls vertically on its own; keep overscroll from bubbling out to a
     page-level pull-to-refresh while dragging at the top edge. */
  touch-action: pan-y;
  overscroll-behavior: contain;
}

/* ── Transition ────────────────────────────────────────────────────── */

.sheet-enter-active,
.sheet-leave-active,
.sheet-enter-active::backdrop,
.sheet-leave-active::backdrop {
  transition: opacity 220ms cubic-bezier(0.32, 0.72, 0, 1);
}

.sheet-enter-active .sheet,
.sheet-leave-active .sheet {
  transition: transform 320ms cubic-bezier(0.32, 0.72, 0, 1);
}

.sheet-enter-from,
.sheet-leave-to,
.sheet-enter-from::backdrop,
.sheet-leave-to::backdrop {
  opacity: 0;
}

.sheet-enter-from .sheet,
.sheet-leave-to .sheet {
  transform: translateY(100%);
}

/* A reader who asked for less motion gets the sheet already here, rather than
   sliding and fading in at them — and the top layer is the loudest place in
   the app to ignore that setting, because the backdrop fades the WHOLE page.
   Sheet was the one overlay in the library without this block.

   The enter's `opacity: 0` is neutralized as well as its transition, and that
   half is load-bearing rather than tidy. `transition: none` alone still leaves
   `sheet-enter-from` painting a transparent backdrop for the one frame before
   Vue removes the class, and the visual gate captures the top layer from
   exactly there: measured at the instant the suite's own waits are satisfied,
   the backdrop's opacity was 0 in ten trials out of ten. What has been saving
   those captures is Playwright fast-forwarding a PSEUDO-ELEMENT's transition,
   which it does not always win — two captures in forty held a page with no
   backdrop over it, and one of those got blessed and stood as the baseline
   until it was noticed. Under this block there is no fade to catch.

   The leave keeps its `opacity: 0` / `translateY(100%)`: with no transition to
   run, Vue reads a zero duration and unmounts on the next frame, so the sheet
   is simply gone rather than animating away. `after-leave` still fires, which
   is what puts focus back on whatever opened it. */
@media (prefers-reduced-motion: reduce) {
  .sheet-enter-active,
  .sheet-leave-active,
  .sheet-enter-active::backdrop,
  .sheet-leave-active::backdrop,
  .sheet-enter-active .sheet,
  .sheet-leave-active .sheet {
    transition: none;
  }

  .sheet-enter-from,
  .sheet-enter-from::backdrop {
    opacity: 1;
  }

  .sheet-enter-from .sheet {
    transform: translateY(var(--sheet-drag, 0px));
  }
}
</style>
